"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useViewContext } from "./ViewContext";
import type { NavItem as NavItemData } from "./navTypes";
import { motion } from "framer-motion";
import { useAuth } from "../auth/Auth";
import { useSeizeConnectContext } from "../auth/SeizeConnectContext";
import { useTitle } from "@/contexts/TitleContext";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useUnreadIndicator } from "@/hooks/useUnreadIndicator";
import { useNotificationsContext } from "../notifications/NotificationsContext";
import { isNavItemActive } from "./isNavItemActive";
import { useWaveData } from "@/hooks/useWaveData";
import { useWave } from "@/hooks/useWave";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
interface Props {
  readonly item: NavItemData;
}
const NavItem = t0 => {
  const $ = _c(73);
  const {
    item
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    activeView,
    handleNavClick
  } = useViewContext();
  const {
    name
  } = item;
  const {
    icon
  } = item;
  const {
    address,
    seizeConnect
  } = useSeizeConnectContext();
  const isLogoItem = name === "Home";
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    });
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const waveIdFromQuery = t1;
  let t2;
  if ($[3] !== waveIdFromQuery) {
    t2 = {
      waveId: waveIdFromQuery,
      onWaveNotFound: _temp
    };
    $[3] = waveIdFromQuery;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: waveData
  } = useWaveData(t2);
  const {
    isDm: isCurrentWaveDmValue
  } = useWave(waveData);
  const {
    connectedProfile
  } = useAuth();
  let t3;
  if ($[5] !== connectedProfile?.handle || $[6] !== connectedProfile?.normalised_handle) {
    t3 = (connectedProfile?.normalised_handle ?? connectedProfile?.handle)?.toLowerCase();
    $[5] = connectedProfile?.handle;
    $[6] = connectedProfile?.normalised_handle;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const normalizedConnectedHandle = t3;
  let t4;
  if ($[8] !== address) {
    t4 = address?.toLowerCase();
    $[8] = address;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const normalizedConnectedAddress = t4;
  const profileSlug = normalizedConnectedHandle ?? normalizedConnectedAddress;
  const profileHref = profileSlug ? `/${profileSlug}` : null;
  const {
    setTitle
  } = useTitle();
  const {
    notifications,
    haveUnreadNotifications
  } = useUnreadNotifications(item.name === "Notifications" ? connectedProfile?.handle ?? null : null);
  const t5 = item.name === "Messages" ? connectedProfile?.handle ?? null : null;
  let t6;
  if ($[10] !== t5) {
    t6 = {
      type: "messages",
      handle: t5
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    hasUnread: hasUnreadMessages
  } = useUnreadIndicator(t6);
  const {
    removeAllDeliveredNotifications
  } = useNotificationsContext();
  let t7;
  if ($[12] !== haveUnreadNotifications || $[13] !== item.name || $[14] !== notifications?.unread_count || $[15] !== removeAllDeliveredNotifications || $[16] !== setTitle) {
    t7 = () => {
      if (item.name !== "Notifications") {
        return;
      }
      setTitle(haveUnreadNotifications ? `(${notifications?.unread_count}) Notifications | 6529.io` : "6529.io");
      if (!haveUnreadNotifications) {
        removeAllDeliveredNotifications();
      }
    };
    $[12] = haveUnreadNotifications;
    $[13] = item.name;
    $[14] = notifications?.unread_count;
    $[15] = removeAllDeliveredNotifications;
    $[16] = setTitle;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = notifications?.unread_count;
  let t9;
  if ($[18] !== haveUnreadNotifications || $[19] !== t8) {
    t9 = [haveUnreadNotifications, t8];
    $[18] = haveUnreadNotifications;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t7, t9);
  if (item.disabled) {
    let t10;
    if ($[21] !== icon || $[22] !== item.iconComponent || $[23] !== item.iconSizeClass || $[24] !== name) {
      t10 = <div className="tw-flex tw-items-center tw-justify-center">{item.iconComponent ? <item.iconComponent className={`${item.iconSizeClass ?? "tw-size-7"} tw-text-iron-500`} /> : <Image src={icon} alt={name} width={24} height={24} unoptimized={true} className={item.iconSizeClass ?? "tw-size-7"} />}</div>;
      $[21] = icon;
      $[22] = item.iconComponent;
      $[23] = item.iconSizeClass;
      $[24] = name;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== name || $[27] !== t10) {
      t11 = <button type="button" aria-label={name} aria-disabled="true" disabled={true} className="tw-pointer-events-none tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-opacity-40 tw-transition-colors focus:tw-outline-none">{t10}</button>;
      $[26] = name;
      $[27] = t10;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    return t11;
  }
  const iconSizeClass = item.iconSizeClass ?? "tw-size-7";
  const isProfileItem = item.kind === "route" && item.name === "Profile";
  let t10;
  if ($[29] !== activeView || $[30] !== isProfileItem || $[31] !== pathname || $[32] !== profileHref) {
    const normalizedPathname = pathname.toLowerCase();
    t10 = isProfileItem && activeView === null && profileHref !== null && (normalizedPathname === profileHref || normalizedPathname.startsWith(`${profileHref}/`));
    $[29] = activeView;
    $[30] = isProfileItem;
    $[31] = pathname;
    $[32] = profileHref;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const isProfileActive = t10;
  let t11;
  if ($[34] !== activeView || $[35] !== isCurrentWaveDmValue || $[36] !== isProfileActive || $[37] !== isProfileItem || $[38] !== item || $[39] !== pathname || $[40] !== searchParams) {
    t11 = isProfileItem ? isProfileActive : isNavItemActive(item, pathname, searchParams, activeView, isCurrentWaveDmValue);
    $[34] = activeView;
    $[35] = isCurrentWaveDmValue;
    $[36] = isProfileActive;
    $[37] = isProfileItem;
    $[38] = item;
    $[39] = pathname;
    $[40] = searchParams;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const isActive = t11;
  let t12;
  if ($[42] !== address || $[43] !== handleNavClick || $[44] !== item || $[45] !== profileHref || $[46] !== seizeConnect) {
    t12 = () => {
      if (item.kind === "route" && item.name === "Profile") {
        if (!address) {
          seizeConnect();
          return;
        }
        handleNavClick({
          ...item,
          href: profileHref ?? item.href
        });
        return;
      }
      handleNavClick(item);
    };
    $[42] = address;
    $[43] = handleNavClick;
    $[44] = item;
    $[45] = profileHref;
    $[46] = seizeConnect;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  const handleClick = t12;
  const t13 = isActive ? "page" : undefined;
  let t14;
  if ($[48] !== isActive || $[49] !== isLogoItem) {
    t14 = isActive && <motion.div layoutId="nav-indicator" className={`tw-absolute tw-left-0 tw-top-0 tw-h-0.5 tw-w-full tw-rounded-full tw-bg-white ${isLogoItem ? "tw-top-1" : ""}`} />;
    $[48] = isActive;
    $[49] = isLogoItem;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== icon || $[52] !== iconSizeClass || $[53] !== isActive || $[54] !== item.iconComponent || $[55] !== name) {
    t15 = item.iconComponent ? <item.iconComponent className={`${iconSizeClass} ${isActive ? "tw-text-white" : "tw-text-iron-500"}`} /> : <Image src={icon} alt={name} width={24} height={24} unoptimized={true} className={iconSizeClass} />;
    $[51] = icon;
    $[52] = iconSizeClass;
    $[53] = isActive;
    $[54] = item.iconComponent;
    $[55] = name;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== haveUnreadNotifications || $[58] !== item.name) {
    t16 = item.name === "Notifications" && haveUnreadNotifications && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[57] = haveUnreadNotifications;
    $[58] = item.name;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== hasUnreadMessages || $[61] !== item.name) {
    t17 = item.name === "Messages" && hasUnreadMessages && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[60] = hasUnreadMessages;
    $[61] = item.name;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== t15 || $[64] !== t16 || $[65] !== t17) {
    t18 = <div className="tw-relative tw-flex tw-items-center tw-justify-center">{t15}{t16}{t17}</div>;
    $[63] = t15;
    $[64] = t16;
    $[65] = t17;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== handleClick || $[68] !== name || $[69] !== t13 || $[70] !== t14 || $[71] !== t18) {
    t19 = <button type="button" aria-label={name} aria-current={t13} onClick={handleClick} className="tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-transition-colors focus:tw-outline-none">{t14}{t18}</button>;
    $[67] = handleClick;
    $[68] = name;
    $[69] = t13;
    $[70] = t14;
    $[71] = t18;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  return t19;
};
export default NavItem;
function _temp() {}